import { useTranslation } from 'react-i18next'
import { cn } from 'src/helpers/style'

import {
  AppIcon,
  AppIconNames,
  Button,
  Typography,
  TypographyVariant,
} from 'src/components/atoms'

import { useKnapWidget } from 'src/app/ask-ai/hooks/use-knap-widget'

import { KnapWidgetContainer } from './knap-widget-container'

export interface IAskAiPanelProps {
  isOpen: boolean
  isExpanded: boolean
  onClose: () => void
  onToggleExpand: () => void
  id?: string
}

const panelBaseClassName =
  'fixed bottom-0 left-0 right-0 top-0 z-50 flex flex-col overflow-hidden bg-surface-chat font-inter transition-all duration-300 ease-out motion-reduce:transition-none md:rounded-2xl md:border md:border-chat md:shadow-xl'

// Both layout variants define explicit lengths for every transitioning
// property (left/right/top/bottom/max-w/max-h) so the wrapper's
// transition-all has interpolatable values to animate between when
// expanding or collapsing. Width is implied from left + right and capped
// by max-width; max-h on compact is a no-op cap that exists only to give
// the property a length for the transition.
const panelCompactClassName =
  'md:bottom-6 md:left-[calc(100vw-424px)] md:right-6 md:top-[97px] md:max-w-[400px] md:max-h-[2000px]'

const panelExpandedClassName =
  'md:bottom-6 md:left-12 md:right-12 md:top-[105px] md:mx-auto md:max-w-[1344px] md:max-h-[880px]'

const panelOpenClassName =
  'pointer-events-auto translate-y-0 scale-100 opacity-100'

const panelClosedClassName =
  'pointer-events-none -translate-y-2 scale-95 opacity-0'

const iconButtonClassName =
  'absolute top-4 z-10 inline-flex h-6 w-6 items-center justify-center rounded text-content-primary transition-colors hover:bg-state-hover-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-chat'

const closeButtonClassName = cn(iconButtonClassName, 'right-4')

const expandButtonClassName = cn(
  iconButtonClassName,
  'right-12 hidden md:inline-flex',
)

export const AskAiPanel = ({
  isOpen,
  isExpanded,
  onClose,
  onToggleExpand,
  id,
}: IAskAiPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const { containerRef, status, retry } = useKnapWidget({ isOpen })

  return (
    <aside
      id={id}
      role="dialog"
      aria-label={t('askAi.panelTitle')}
      aria-hidden={!isOpen}
      className={cn(
        panelBaseClassName,
        isExpanded ? panelExpandedClassName : panelCompactClassName,
        isOpen ? panelOpenClassName : panelClosedClassName,
      )}
    >
      <button
        type="button"
        aria-label={isExpanded ? t('askAi.collapse') : t('askAi.expand')}
        aria-pressed={isExpanded}
        onClick={onToggleExpand}
        className={expandButtonClassName}
      >
        <AppIcon
          name={isExpanded ? AppIconNames.arrowsIn : AppIconNames.arrowsOut}
          size={20}
          color="currentColor"
        />
      </button>

      <button
        type="button"
        aria-label={t('askAi.close')}
        onClick={onClose}
        className={closeButtonClassName}
      >
        <AppIcon name={AppIconNames.close} size={20} color="currentColor" />
      </button>

      {status === 'error' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10">
          <Typography
            variant={TypographyVariant.bodySm}
            className="text-center text-content-secondary"
          >
            {t('askAi.errorMessage')}
          </Typography>
          <Button label={t('askAi.retry')} onClick={retry} disabled={!isOpen} />
        </div>
      ) : (
        <KnapWidgetContainer containerRef={containerRef} />
      )}
    </aside>
  )
}
